'use client';

import './nav-account.scss';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { AuthStatusEnum, useAuth } from '@sola/auth-sdk/store.js';
import { getPublicConfig } from '@sola/config/lib/getters.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

import { Avatar } from '../avatar/avatar';

type NavAccountProps = {
  userMenu?: React.ReactNode;
  urlLogin?: string;
  urlAccountHome?: string;
};

// These hardcoded links will be used if no user menu is supplied from Contentful.
export const FallbackUserMenu = () => {
  const { t } = useTranslation(['priority', 'core']);
  const { NEXT_PUBLIC_BASE_URL_MYACCOUNT } = getPublicConfig();
  const { actions: authActions } = useAuth('actions');

  const logOut = async () => {
    trackClick(t('priority:nav.logOut'));
    authActions.logOut();
  };
  return (
    <>
      <DropdownMenuItem className="flex w-full align-start">
        <span className="flex px-comfy-sm py-snug-xl gap-comfy-xs items-center">
          <ArkIcon iconName="home-03" className="NavAccount__icon--small" />
          <LinkStyle noUnderline noPadding purpose="neutral">
            <Link
              href={NEXT_PUBLIC_BASE_URL_MYACCOUNT || '#'}
              className="NavAccount__menu-item w-full py-comfy-xs px-comfy-sm"
            >
              {t('core:actions.goToMyAccount')}
            </Link>
          </LinkStyle>
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem className="flex w-full align-start">
        <span className="flex px-comfy-sm py-snug-xl gap-comfy-xs items-center">
          <ArkIcon iconName="log-out-01" className="NavAccount__icon--small" />
          <LinkStyle noUnderline noPadding purpose="negative">
            <button type="button" onClick={logOut}>
              {t('priority:nav.logOut')}
            </button>
          </LinkStyle>
        </span>
      </DropdownMenuItem>
    </>
  );
};

export const NavAccount: React.FC<NavAccountProps> = ({
  userMenu,
  urlLogin,
  urlAccountHome,
}) => {
  const { t } = useTranslation();
  const { currentStatus } = useAuth('currentStatus');
  const { NEXT_PUBLIC_BASE_URL_MYACCOUNT } = getPublicConfig();

  if (currentStatus !== AuthStatusEnum.Authenticated) {
    return (
      <Link
        type="button"
        href={urlLogin || NEXT_PUBLIC_BASE_URL_MYACCOUNT || '#'}
        className="NavAccount__avatar flex items-center justify-center"
        title={t('priority:header.myAccount')}
        aria-label={t('priority:header.myAccount')}
      >
        <Avatar variant="minimal" />
      </Link>
    );
  }

  // NOTE: For skippy, we want to keep the existing solution, because we don't have a MyCompassion yet!
  const isLegacySkippySolution = !userMenu;
  if (isLegacySkippySolution) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="NavAccount__avatar flex p-comfy-xs"
            title={t('priority:header.myAccount')}
            aria-label={t('priority:header.myAccount')}
          >
            <Avatar variant="minimal" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="NavAccount__menu flex flex-col items-end mt-snug-lg py-snug-xl"
        >
          <FallbackUserMenu />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // @TODO: Handling supporter name in another branch
  const supporterName = undefined;

  return (
    <>
      <Link
        type="button"
        href={urlAccountHome || NEXT_PUBLIC_BASE_URL_MYACCOUNT || '#'}
        className="NavAccount__avatar hidden lg:flex items-center justify-center"
        title={t('priority:header.myAccount')}
        aria-label={t('priority:header.myAccount')}
      >
        <Avatar variant="minimal" name={supporterName} />
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="NavAccount__avatar flex lg:hidden p-comfy-xs"
            title={t('priority:header.myAccount')}
            aria-label={t('priority:header.myAccount')}
          >
            <Avatar variant="minimal" name={supporterName} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="NavAccount__menu flex flex-col items-end mt-snug-lg py-snug-xl"
        >
          {userMenu || <FallbackUserMenu />}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
